// @flow

import * as React from 'react';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectCartProductOwner,
  type DecoratedProduct,
  type ProductKind as TProductKind,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Alert, Base } from '@gandi/ui';

import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks';

import ProductsList from './ProductsList';
import { DomainBundleRow } from './components/DomainBundleRow';
import DomainMiscFeesRow from './components/DomainMiscFeesRow';
import { MailboxRenewGroup } from './components/MailboxRenewGroup';
import { ProductKindHeader } from './components/ProductKindHeader';
import ProductItemBase from './components/components/ProductItemBase';
import { ProductContext } from './modules/ProductContext';
import {
  selectCorporateMiscFeesByKind,
  selectGroupedProductsForKind,
} from './selectors';
import { getProductComponent } from './tools';

import styles from './ProductKind.scss';

type Props = { kind: TProductKind };

// Prevent re-rendering of mailbox product-components when adding/removing
// a mailbox single product.
// Mailbox products are abstracted into the redux state and `product.id`
// changes as the quantity of these products do.
const getProductKey = (product: DecoratedProduct): string =>
  product.productType === 'mailboxv2' &&
  product.process === 'create' &&
  product.payload.fqdn &&
  product.payload.mailbox_type
    ? `mailboxv2-${product.payload.fqdn}-${product.payload.mailbox_type}`
    : `${product.id}`;

export function ProductKind({ kind }: Props): React.MixedElement | null {
  const {
    firstProduct,
    hasWebhosting,
    productItems,
    mailboxRenewGroups,
    cartDomainCreateBundles,
  } = useAppSelector(state => selectGroupedProductsForKind(state, { kind }));

  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const cartProductOwner = useAppSelector(selectCartProductOwner);
  const corporateMiscFeesByKind = useAppSelector(selectCorporateMiscFeesByKind);

  if (!firstProduct) {
    return null;
  }

  const miscFees = corporateMiscFeesByKind[kind];

  return (
    <ProductKindBlock id={`section-${kind}`}>
      <ProductKindHeader
        key={kind}
        kind={kind}
        process={firstProduct.process}
        productType={firstProduct.productType}
        authenticated={authenticated}
        cartProductOwner={cartProductOwner}
        firstProductId={firstProduct.id}
        hasWebhosting={hasWebhosting}
      />

      <ProductsList>
        {productItems.map(product => {
          const ProductComponent = getProductComponent(product.component);

          return (
            <ProductContext.Provider
              value={product}
              key={getProductKey(product)}
            >
              <ErrorBoundary
                fallback={<ProductItemBase.Error product={product} />}
              >
                <ProductComponent product={product} />
              </ErrorBoundary>
            </ProductContext.Provider>
          );
        })}

        <MailboxRenewGroup groupedProducts={mailboxRenewGroups} />

        {cartDomainCreateBundles.length > 0 &&
          cartDomainCreateBundles.map(bundle => (
            <DomainBundleRow bundle={bundle} key={bundle.id} />
          ))}
      </ProductsList>

      {miscFees && (
        <DomainMiscFeesRow
          count={miscFees.count}
          price={miscFees.priceBeforeTaxes}
        />
      )}
    </ProductKindBlock>
  );
}

function ProductKindBlock({
  children,
  id,
}: {
  children: React.Node,
  id: string,
}): React.MixedElement {
  return (
    <Base
      id={id}
      position="relative"
      backgroundColor="surface"
      className={styles.root}
      tagName="li"
    >
      {children}
    </Base>
  );
}

ProductKind.Error = function ProductKindError({
  kind,
}: {
  kind: TProductKind,
}): React.MixedElement {
  const { __ } = useTranslator();

  return (
    <ProductKindBlock id={`section-${kind}-error`}>
      <ProductKindHeader.Error kind={kind} />

      <ProductsList>
        <Alert
          title={__('Error')}
          message={__('An error has occurred. Please try again later.')}
          intent="danger"
          tagName="li"
          withBackground
        />
      </ProductsList>
    </ProductKindBlock>
  );
};
